import { DocReferenceRecord } from "../../reference-extraction/extraction";
import {
  AuditCategory,
  AuditFinding,
  AuditSeverity,
  ReferenceMismatchFinding,
} from "../audit-report";
import { AuditCheckRunner, AuditCheckRunnerContext } from "../run-scan";

/**
 * Options accepted by {@link createReferenceMismatchCheck}.
 *
 * The runner consumes already-computed reference records produced by
 * `extractDocReferences()`. The factory deliberately does not invoke
 * the extractor — that keeps the runner cheap to test with inline
 * fixtures and lets the orchestrator extract references once and
 * feed multiple checks from the same input.
 */
export interface ReferenceMismatchCheckOptions {
  /**
   * The reference records to translate into findings. Each record
   * carries the `isKnown` flag the extractor computed against the
   * supplied known-symbols set; the runner produces a finding for
   * every record where `isKnown` is `false`.
   */
  readonly records: ReadonlyArray<DocReferenceRecord>;

  /**
   * Stable name surfaced via {@link AuditCheckRunner.name}. Defaults
   * to `"referenceMismatches"` so logs match the audit-report
   * category key.
   *
   * @default "referenceMismatches"
   */
  readonly name?: string;

  /**
   * Optional set of symbols whose signature changed between the
   * baseline and the current revision. References to one of these
   * symbols produce a `signature-changed` finding rather than the
   * default `unknown-symbol` finding (the symbol resolves but its
   * type signature differs from the cached snapshot, per the
   * audit-report schema). Empty / omitted means no signature-change
   * findings are produced.
   */
  readonly signatureChangedSymbols?: ReadonlyArray<string>;
}

/**
 * Map a single {@link DocReferenceRecord} to zero or one audit
 * findings. Exported so callers can reuse the record-to-finding
 * mapping without going through a runner.
 *
 * Severity mapping:
 *
 * - `unknown-symbol` (record carries `isKnown: false`) — `advisory`.
 *   The doc mentions a symbol that does not appear in the supplied
 *   known-symbols set; a human reviewer should rename the mention or
 *   remove it. The matching `signature-changed` case only fires when
 *   the caller passes the symbol via `signatureChangedSymbols`.
 * - Records with `isKnown: true` and no signature change produce no
 *   findings.
 */
export function referenceRecordToFinding(
  record: DocReferenceRecord,
  signatureChangedSymbols: ReadonlySet<string>,
): ReferenceMismatchFinding | undefined {
  if (signatureChangedSymbols.has(record.symbol)) {
    return {
      category: AuditCategory.ReferenceMismatches,
      severity: AuditSeverity.Advisory,
      location: { file: record.docPath, line: record.line },
      subject: record.symbol,
      details: `Inline reference \`${record.symbol}\` resolves to a symbol whose signature has changed; review the surrounding prose.`,
      mismatch: "signature-changed",
      symbol: record.symbol,
    };
  }
  if (!record.isKnown) {
    return {
      category: AuditCategory.ReferenceMismatches,
      severity: AuditSeverity.Advisory,
      location: { file: record.docPath, line: record.line },
      subject: record.symbol,
      details: `Inline reference \`${record.symbol}\` does not match any known public export.`,
      mismatch: "unknown-symbol",
      symbol: record.symbol,
    };
  }
  return undefined;
}

/**
 * Build an {@link AuditCheckRunner} that emits doc-reference
 * mismatch findings from a precomputed list of
 * {@link DocReferenceRecord}. The runner is synchronous and
 * idempotent — calling `run()` twice produces identical findings.
 */
export function createReferenceMismatchCheck(
  options: ReferenceMismatchCheckOptions,
): AuditCheckRunner {
  const records = options.records;
  const signatureChangedSymbols = new Set(
    options.signatureChangedSymbols ?? [],
  );
  return {
    name: options.name ?? "referenceMismatches",
    run(_context: AuditCheckRunnerContext): ReadonlyArray<AuditFinding> {
      const findings: Array<ReferenceMismatchFinding> = [];
      for (const record of records) {
        const finding = referenceRecordToFinding(
          record,
          signatureChangedSymbols,
        );
        if (finding !== undefined) {
          findings.push(finding);
        }
      }
      return findings;
    },
  };
}
